/**
 * SyncQueue — Offline-first background sync for user preferences.
 *
 * Queues preference changes and batches them by endpoint:
 * - Coalescing: fields targeting the same endpoint merge into one entry
 * - One API call per endpoint (not per field)
 * - Exponential backoff with jitter on failure, gated per endpoint —
 *   a failing endpoint never blocks other endpoints' writes
 * - Persistent queue across app restarts (AsyncStorage-backed)
 * - Automatic flush on network restore, app foreground, and heartbeat
 */

import { AppState } from 'react-native';
import type { AppStateStatus, NativeEventSubscription } from 'react-native';
import { createLogger } from '../utils/logger';
import { apiClient } from './apiClient';
import NetInfo from '@react-native-community/netinfo';
import type { NetInfoSubscription } from '@react-native-community/netinfo';
import type { TransportError, DomainErrorBase } from '../generated/api/endpoint-errors';
import {
  type SyncEndpoint,
  type SyncableField,
  type SyncQueuePersistenceData,
  FIELD_TO_ENDPOINT,
  QUEUE_CONFIG,
  dispatchSync,
  SyncQueuePersistenceStorage,
} from '../generated/sync/sync-config';

const log = createLogger('SyncQueue');

// ─── Constants (from generated sync-config) ──────────────────────────────────

const { schemaVersion: QUEUE_VERSION } = QUEUE_CONFIG;
const { initialDelay: INITIAL_DELAY_MS, maxDelay: MAX_DELAY_MS, backoffFactor: BACKOFF_FACTOR, jitter: JITTER, maxRetries: MAX_RETRIES } = QUEUE_CONFIG.retry;
const DEBOUNCE_MS = QUEUE_CONFIG.debounce;
const HEARTBEAT_MS = QUEUE_CONFIG.heartbeat;

interface SyncQueueEntry {
  endpoint: SyncEndpoint;
  fields: Record<string, unknown>;
  enqueuedAt: number;
  retries: number;
}

interface PersistedQueue {
  version: number;
  entries: Array<{ endpoint: SyncEndpoint; fields: Record<string, unknown>; enqueuedAt: number; retries: number }>;
}

// ─── Error classification ────────────────────────────────────────────────────

/** Classify a thrown Result error from dispatchSync for retry decisions. */
function classifySyncError(error: unknown): 'retry' | 'drop' | 'auth_stop' {
  // dispatchSync throws the failed Result object: { ok: false, kind, error }
  const result = error as
    | { ok: false; kind: 'domain'; error: DomainErrorBase & { code: string; category: string } }
    | { ok: false; kind: 'transport'; error: TransportError };

  if (result && typeof result === 'object' && 'ok' in result && result.ok === false) {
    if (result.kind === 'domain') {
      if (result.error.category === 'AUTH') return 'auth_stop';
      if (result.error.category === 'UNEXPECTED') return 'retry';
      // VALIDATION, BUSINESS — bad data, retrying won't help
      return 'drop';
    }
    // Transport errors are retryable (OFFLINE, TIMEOUT, SERVER_UNREACHABLE, SERVER_ERROR)
    // except UNKNOWN which is not
    if (result.kind === 'transport') {
      return result.error.retryable ? 'retry' : 'drop';
    }
  }

  // Unknown error shape — retry as a safe default
  return 'retry';
}

// ─── Backoff calculation ─────────────────────────────────────────────────────

function calculateDelay(retries: number): number {
  const base = Math.min(INITIAL_DELAY_MS * Math.pow(BACKOFF_FACTOR, retries), MAX_DELAY_MS);
  const jitterAmount = base * JITTER * (Math.random() * 2 - 1);
  return Math.max(0, base + jitterAmount);
}

// ─── SyncQueue service ──────────────────────────────────────────────────────

class SyncQueueService {
  private queue = new Map<SyncEndpoint, SyncQueueEntry>();
  // Per-endpoint backoff gates (epoch ms before which the endpoint is not retried).
  // In-memory only — persisting it would change the persisted queue shape and
  // force a QUEUE_VERSION bump, which discards users' queues on update.
  private nextAttemptAt = new Map<SyncEndpoint, number>();
  private processing = false;
  private paused = false;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private appStateSubscription: NativeEventSubscription | null = null;
  private netInfoUnsub: NetInfoSubscription | null = null;
  private initialized = false;

  /**
   * Load persisted queue and register event listeners.
   * Call once after authentication is confirmed.
   */
  async initialize(): Promise<void> {
    if (this.initialized) return;

    // Load persisted queue
    try {
      const loaded = await SyncQueuePersistenceStorage.load();
      if (loaded) {
        const persisted = loaded as PersistedQueue;

        // Version guard: if format changed, discard stale queue (reconciliation in fetchProfile will re-enqueue diffs)
        if (persisted.version !== QUEUE_VERSION) {
          log.info('Discarding stale sync queue', { oldVersion: persisted.version, newVersion: QUEUE_VERSION });
          await SyncQueuePersistenceStorage.clear();
        } else {
          for (const entry of persisted.entries) {
            this.queue.set(entry.endpoint, entry);
          }
        }
        if (this.queue.size > 0) {
          log.info('Loaded persisted sync queue', { size: this.queue.size });
        }
      }
    } catch (error) {
      log.warn('Failed to load persisted sync queue', { error });
    }

    this.paused = false;
    this.initialized = true;

    // AppState listener — flush on foreground
    this.appStateSubscription = AppState.addEventListener('change', this.handleAppState);

    // Network listener — flush on connectivity restored (also unpauses after 401 recovery)
    this.netInfoUnsub = NetInfo.addEventListener((state) => {
      if (state.isConnected) {
        if (this.paused) {
          log.info('Network restored, unpausing sync queue');
          this.paused = false;
        }
        if (this.queue.size > 0) {
          this.flush({ resetBackoff: true });
        }
      }
    });

    // Heartbeat — safety net for missed events
    this.heartbeatTimer = setInterval(() => {
      if (this.queue.size > 0 && !this.processing && !this.paused) {
        log.debug('Heartbeat: flushing sync queue', { size: this.queue.size });
        this.flush();
      }
    }, HEARTBEAT_MS);

    // Process any items from previous session
    if (this.queue.size > 0) {
      this.flush();
    }
  }

  /**
   * Enqueue a field for background sync.
   * The field is merged into the endpoint's batch entry (coalescing by endpoint).
   */
  async enqueue(field: SyncableField, value: unknown): Promise<void> {
    const endpoint = FIELD_TO_ENDPOINT[field];

    const existing = this.queue.get(endpoint);
    if (existing) {
      // Merge field into existing endpoint batch
      existing.fields[field] = value;
      existing.enqueuedAt = Date.now();
    } else {
      // New endpoint batch
      this.queue.set(endpoint, {
        endpoint,
        fields: { [field]: value },
        enqueuedAt: Date.now(),
        retries: 0,
      });
    }

    await this.persist();
    this.scheduleDebounced();
  }

  /**
   * Check if there are pending (unsent) changes for a given endpoint.
   * Used by stores to decide whether to keep local values during reconciliation.
   */
  hasPendingForEndpoint(endpoint: SyncEndpoint): boolean {
    return this.queue.has(endpoint);
  }

  /**
   * Trigger immediate queue processing (if not already running).
   * `resetBackoff` clears all per-endpoint backoff gates first — used when the
   * world changed (network restored, app foregrounded) and waiting out a gate
   * computed under the old conditions would be pointless.
   */
  flush(options?: { resetBackoff?: boolean }): void {
    if (options?.resetBackoff) this.nextAttemptAt.clear();
    if (this.processing || this.paused || this.queue.size === 0) return;
    this.clearTimers();
    this.processQueue();
  }

  /**
   * Stop processing (e.g., on auth failure or logout).
   */
  pause(): void {
    this.paused = true;
    this.clearTimers();
  }

  /**
   * Resume processing after a pause (e.g., E2E sync-behavior tests, or
   * 401 recovery). Pending entries flush on the next trigger.
   */
  resume(): void {
    this.paused = false;
  }

  /**
   * Clear all queue entries and persisted data.
   */
  async clear(): Promise<void> {
    this.queue.clear();
    this.nextAttemptAt.clear();
    this.clearTimers();
    await this.persist();
  }

  /**
   * Tear down listeners and timers. Call on app shutdown or logout.
   */
  destroy(): void {
    this.pause();
    this.nextAttemptAt.clear();
    this.appStateSubscription?.remove();
    this.appStateSubscription = null;
    this.netInfoUnsub?.();
    this.netInfoUnsub = null;
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    this.initialized = false;
  }

  // ── Internal ─────────────────────────────────────────────────────────────

  private handleAppState = (state: AppStateStatus) => {
    if (state === 'active' && this.queue.size > 0 && !this.paused) {
      log.debug('App foregrounded, flushing sync queue');
      this.flush({ resetBackoff: true });
    }
  };

  private scheduleDebounced(): void {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      if (!this.paused && this.queue.size > 0) {
        this.flush();
      }
    }, DEBOUNCE_MS);
  }

  private async processQueue(): Promise<void> {
    if (this.processing || this.paused) return;
    this.processing = true;

    // Snapshot entries sorted by enqueuedAt (FIFO)
    const entries = Array.from(this.queue.values()).sort(
      (a, b) => a.enqueuedAt - b.enqueuedAt,
    );

    // True when at least one entry stays queued behind a backoff gate — the
    // signal to schedule a follow-up pass after the loop.
    let hasGatedEntries = false;

    for (const entry of entries) {
      // Re-check in case queue was modified during processing
      const current = this.queue.get(entry.endpoint);
      if (this.paused || !current || current.enqueuedAt !== entry.enqueuedAt) continue;

      // Endpoint still inside its backoff window — leave it queued and move on,
      // so one failing endpoint never blocks the others.
      const dueAt = this.nextAttemptAt.get(entry.endpoint) ?? 0;
      if (dueAt > Date.now()) {
        hasGatedEntries = true;
        continue;
      }

      try {
        // dispatchSync sends all fields for this endpoint in one API call
        await dispatchSync(apiClient, entry.endpoint, entry.fields);

        // Only delete if the entry hasn't been replaced by a newer enqueue during the API call
        const afterDispatch = this.queue.get(entry.endpoint);
        if (afterDispatch && afterDispatch.enqueuedAt === entry.enqueuedAt) {
          this.queue.delete(entry.endpoint);
        }
        this.nextAttemptAt.delete(entry.endpoint);
        log.debug('Synced endpoint batch', { endpoint: entry.endpoint, fields: Object.keys(entry.fields) });
      } catch (error) {
        const action = classifySyncError(error);

        if (action === 'auth_stop') {
          log.warn('Auth failure during sync, pausing queue', {
            queueDepth: this.queue.size,
            endpoints: Array.from(this.queue.keys()),
          });
          this.paused = true;
          break;
        }

        if (action === 'drop') {
          this.queue.delete(entry.endpoint);
          this.nextAttemptAt.delete(entry.endpoint);
          log.warn('Dropping sync entry (non-retryable)', error, {
            endpoint: entry.endpoint,
            fields: Object.keys(entry.fields),
            retries: entry.retries,
          });
          continue;
        }

        // action === 'retry'
        entry.retries += 1;
        if (entry.retries >= MAX_RETRIES) {
          this.queue.delete(entry.endpoint);
          this.nextAttemptAt.delete(entry.endpoint);
          log.warn('Sync entry exhausted retries', {
            endpoint: entry.endpoint,
            fields: Object.keys(entry.fields),
            retries: entry.retries,
            maxRetries: MAX_RETRIES,
          });
          continue;
        }

        // Gate this endpoint with backoff and keep processing the rest.
        log.debug('Sync failed, will retry', {
          endpoint: entry.endpoint,
          retries: entry.retries,
        });
        this.queue.set(entry.endpoint, entry);
        this.nextAttemptAt.set(entry.endpoint, Date.now() + calculateDelay(entry.retries));
        hasGatedEntries = true;
      }
    }

    this.processing = false;
    await this.persist();

    if (hasGatedEntries && !this.paused) {
      this.scheduleRetryPass();
    }

    if (this.queue.size === 0) {
      log.info('Sync queue drained', { totalEntries: entries.length });
    }
  }

  /** Schedule the next processing pass for when the soonest backoff gate expires. */
  private scheduleRetryPass(): void {
    if (this.retryTimer) return;
    let soonest = Infinity;
    for (const endpoint of this.queue.keys()) {
      const dueAt = this.nextAttemptAt.get(endpoint) ?? 0;
      if (dueAt < soonest) soonest = dueAt;
    }
    if (soonest === Infinity) return;
    const delay = Math.max(0, soonest - Date.now());
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      if (!this.paused && this.queue.size > 0) {
        this.processQueue();
      }
    }, delay);
  }

  private clearTimers(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
  }

  private async persist(): Promise<void> {
    try {
      const data: PersistedQueue = {
        version: QUEUE_VERSION,
        entries: Array.from(this.queue.values()),
      };
      await SyncQueuePersistenceStorage.save(data as SyncQueuePersistenceData);
    } catch (error) {
      log.warn('Failed to persist sync queue', { error });
    }
  }
}

// ─── Singleton ───────────────────────────────────────────────────────────────

export const syncQueue = new SyncQueueService();
